import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, Heart, MessageCircle, Share2, TrendingUp, MapPin, Calendar, Award } from 'lucide-react'
import api from '../services/api'

interface AnalyticsData {
  totalViews: number
  totalLikes: number
  totalComments: number
  totalShares: number
  engagementRate: number
  viewsTrend: number // percentage change
  likesTrend: number
  commentsTrend: number
  sharesTrend: number
  topPosts: Array<{
    postId: string
    mediaUrl: string
    views: number
    likes: number
    comments: number
    shares: number
  }>
  geographicBreakdown: Array<{
    city: string
    state: string
    country: string
    views: number
    percentage: number
  }>
  weeklyData: Array<{
    date: string
    views: number
    engagement: number
  }>
}

export default function Analytics() {
  const navigate = useNavigate()
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')

  useEffect(() => {
    loadAnalytics()
  }, [timeRange])

  const loadAnalytics = async () => {
    try {
      setIsLoading(true)
      const result = await api.getAnalytics(timeRange)

      if (result.success && result.data) {
        setData(result.data)
      }
    } catch (error) {
      console.error('Error loading analytics:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  const getTrendColor = (trend: number): string => {
    if (trend > 0) return 'text-green-600'
    if (trend < 0) return 'text-red-600'
    return 'text-gray-600'
  }

  const getTrendIcon = (trend: number) => {
    if (trend > 0) return '↗'
    if (trend < 0) return '↘'
    return '→'
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-onrecord-orange"></div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">No analytics data available</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-onrecord-orange text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-700 hover:text-onrecord-orange rounded-full p-1 hover:bg-orange-50"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Analytics</h1>
              <p className="text-xs text-gray-500">Your content performance</p>
            </div>
          </div>

          {/* Time Range Selector */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {(['7d', '30d', '90d', 'all'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  timeRange === range
                    ? 'bg-white text-onrecord-orange shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {range === '7d' ? '7 Days' :
                 range === '30d' ? '30 Days' :
                 range === '90d' ? '90 Days' :
                 'All Time'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Total Views */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-blue-600" />
              </div>
              <span className={`text-sm font-medium ${getTrendColor(data.viewsTrend)}`}>
                {getTrendIcon(data.viewsTrend)} {Math.abs(data.viewsTrend)}%
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(data.totalViews)}</p>
            <p className="text-sm text-gray-600">Total Views</p>
          </div>

          {/* Total Likes */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-red-600" />
              </div>
              <span className={`text-sm font-medium ${getTrendColor(data.likesTrend)}`}>
                {getTrendIcon(data.likesTrend)} {Math.abs(data.likesTrend)}%
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(data.totalLikes)}</p>
            <p className="text-sm text-gray-600">Total Likes</p>
          </div>

          {/* Total Comments */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <span className={`text-sm font-medium ${getTrendColor(data.commentsTrend)}`}>
                {getTrendIcon(data.commentsTrend)} {Math.abs(data.commentsTrend)}%
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(data.totalComments)}</p>
            <p className="text-sm text-gray-600">Comments</p>
          </div>

          {/* Total Shares */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Share2 className="w-5 h-5 text-purple-600" />
              </div>
              <span className={`text-sm font-medium ${getTrendColor(data.sharesTrend)}`}>
                {getTrendIcon(data.sharesTrend)} {Math.abs(data.sharesTrend)}%
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatNumber(data.totalShares)}</p>
            <p className="text-sm text-gray-600">Shares</p>
          </div>
        </div>

        {/* Engagement Rate */}
        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 mb-1">Engagement Rate</p>
              <p className="text-4xl font-bold">{data.engagementRate.toFixed(1)}%</p>
              <p className="text-white/80 text-sm mt-2">
                {data.totalLikes + data.totalComments + data.totalShares} interactions / {data.totalViews} views
              </p>
            </div>
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>

        {/* Weekly Trend Chart (Simple Bar Chart) */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Weekly Activity</h2>
            <Calendar className="w-5 h-5 text-gray-400" />
          </div>

          <div className="space-y-3">
            {data.weeklyData.map((day, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                  <span className="font-medium text-gray-900">{formatNumber(day.views)} views</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-orange-500 to-red-500 h-full rounded-full transition-all"
                    style={{ width: `${Math.min((day.views / Math.max(...data.weeklyData.map(d => d.views))) * 100, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performing Posts */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Top Posts</h2>
            <Award className="w-5 h-5 text-gray-400" />
          </div>

          <div className="space-y-4">
            {data.topPosts.map((post, index) => (
              <div
                key={post.postId}
                className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                onClick={() => navigate(`/post/${post.postId}`)}
              >
                {/* Rank Badge */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  index === 0 ? 'bg-yellow-100 text-yellow-700' :
                  index === 1 ? 'bg-gray-100 text-gray-700' :
                  index === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-gray-50 text-gray-600'
                }`}>
                  {index + 1}
                </div>

                {/* Media Preview */}
                <img
                  src={post.mediaUrl}
                  alt="Post"
                  className="w-16 h-16 rounded-lg object-cover"
                />

                {/* Stats */}
                <div className="flex-1 grid grid-cols-4 gap-2">
                  <div>
                    <p className="text-xs text-gray-500">Views</p>
                    <p className="text-sm font-semibold">{formatNumber(post.views)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Likes</p>
                    <p className="text-sm font-semibold">{formatNumber(post.likes)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Comments</p>
                    <p className="text-sm font-semibold">{formatNumber(post.comments)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Shares</p>
                    <p className="text-sm font-semibold">{formatNumber(post.shares)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Geographic Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Geographic Breakdown</h2>
            <MapPin className="w-5 h-5 text-gray-400" />
          </div>

          <div className="space-y-3">
            {data.geographicBreakdown.map((location, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{location.city}</span>
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-600">{location.state}, {location.country}</span>
                  </div>
                  <span className="text-gray-600">{formatNumber(location.views)} views ({location.percentage}%)</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 h-full rounded-full transition-all"
                    style={{ width: `${location.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
